import React from "react";
import { Empty, Pagination } from "antd5";
import { SorterResult, TableRowSelection as AntTableRowSelection } from "antd5/es/table/interface";

import { TextLink } from "components/actions/Links";
import {
  convertRecordFiltersToNoticeFilters,
  generateNoticeSearchUrl,
} from "components/notices/utils";
import SignalsContainer from "components/tags/SignalsContainer";
import Button from "components/ui/button/Button";
import { Table } from "lib/core_components/Table";
import { ColumnType, commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { createUseDebounce } from "lib/debounce";
import { SortState } from "lib/search/types";
import { LeadSignal, SignalCategory, SupplierSummary } from "lib/types/models";
import { EM_DASH } from "lib/utils";
import { graphql } from "../../../lib/generated/app-service-gql";
import {
  SuppliersForBuyerReportRequest,
  SuppliersForBuyerReportRequest_SignalSortEnum_0,
  SuppliersForBuyerReportRequest_SortEnum_0,
  SuppliersForBuyerReportRequest_SortOrderEnum_0,
  SuppliersForBuyerReportResponse,
} from "../../../lib/generated/app-service-gql/graphql";
import { useGraphQlClient } from "../../../lib/hooks/api/useGraphQlClient";

import css from "./SupplierTable.module.scss";

const LEAD_SIGNAL_SORT_COLUMN = "lead_signals";

type SortStateWithSignal = SortState & {
  signal_sort?: "ASC" | "DESC";
};

export type PaginationConfig = {
  pageSize: number;
  current: number;
};

export type TableRowSelection = {
  selectedRowKeys: number[];
  onChange: (
    selectedKeys: number[],
    newItems: Record<string, SupplierSummary & AwardsPerSupplier>,
  ) => void;
};

type SupplierFilters = {
  text?: string;
};

type Props = {
  companyGuid: string;
  teamId: string | undefined;
  excludeSignalCategories?: SignalCategory[];
  filters: SupplierFilters;
  initialSort: SortStateWithSignal;
  emptyStateMessage: string;
  handlePageChange: (page: number, pageSize?: number) => void;
  paginationState: PaginationConfig;
  buyerGuid: string;
  buyerId: number;
  rowSelectionConfig: TableRowSelection;
};

export type AwardsPerSupplier = {
  award_count: number;
  latest_activity: string;
  signals: LeadSignal[];
};

type AwardsPerSuppliers = Record<number, AwardsPerSupplier>;

function ViewAwardsLink({ s, buyerGuid }: { s: SupplierSummary; buyerGuid: string }) {
  const filters = convertRecordFiltersToNoticeFilters({
    searchMode: "AWARDS",
    supplierIds: [s.id],
    buyerGuids: [buyerGuid],
  });

  return (
    <Button as="a" buttonType="tertiary" href={generateNoticeSearchUrl(filters)} target="_blank">
      View awards
    </Button>
  );
}

export const getSortOrder = (
  sort: SortStateWithSignal,
  key: string,
): "ascend" | "descend" | undefined => {
  if (sort.signal_sort) {
    if (key === LEAD_SIGNAL_SORT_COLUMN) {
      return sort.signal_sort === "ASC" ? "ascend" : "descend";
      // if `signal_sort` is defined it means it overrides the regular sort and we won't show sort.field as the sorted column
    } else return undefined;
  } else if (sort.field === key) {
    return sort.order === "ASC" ? "ascend" : "descend";
  } else return undefined;
};

function getSupplierColumns(
  sort: SortStateWithSignal,
  buyerGuid: string,
  awardStatsPerSupplier?: AwardsPerSuppliers,
): ColumnType<SupplierSummary>[] {
  const name: ColumnType<SupplierSummary> = {
    ...commonTableColumns.expandableColumn,
    title: "Supplier Name",
    dataIndex: "name",
    key: "name",
    render: (_: unknown, s: SupplierSummary) => (
      // Prefer the canonical GUID URL; older payloads without a guid fall back to the
      // numeric id, which still resolves via redirect.
      <TextLink to={`/suppliers/${s.guid ?? s.id}`}>{s.name}</TextLink>
    ),
    sorter: true,
    sortOrder: getSortOrder(sort, "name"),
  };

  const isSME = {
    ...commonTableColumns.isSMEColumn,
    title: "Is SME",
    render: (_: unknown, s: SupplierSummary) =>
      s.is_sme ? "Yes" : s.is_sme === false ? "No" : EM_DASH,
    key: "is_sme",
    sorter: true,
    width: 182,
    sortOrder: getSortOrder(sort, "is_sme"),
  };

  const signals: ColumnType<SupplierSummary> = {
    ...commonTableColumns.signalsColumn,
    title: "Signals",
    key: "lead_signals",
    render: (_: unknown, s: SupplierSummary) => {
      const signals = awardStatsPerSupplier?.[s.id] ? awardStatsPerSupplier[s.id].signals : [];
      return signals?.length > 0 ? (
        <SignalsContainer
          requiredDataType="BUYERS" // They'll only be hidden if the user doesn't meet the requiredDataTypes
          redactedSignalCategories={["Competitors", "Partners"]}
          signals={signals}
          contextSource="In-row"
        />
      ) : null;
    },
    sorter: true,
    sortOrder: getSortOrder(sort, "lead_signals"),
    sizeConfig: {
      xlarge: 250,
      large: 250,
      medium: 220,
      small: 190,
    },
    // think twice before removing PRODUCT_TOUR_*
    className: "PRODUCT_TOUR_supplierSignalCell",
  };

  const awardCount = {
    title: "Number of Awards",
    render: (_: unknown, s: SupplierSummary) =>
      (awardStatsPerSupplier?.[s.id] && awardStatsPerSupplier[s.id].award_count) || EM_DASH,
    key: "award_count",
    sorter: true,
    sortOrder: getSortOrder(sort, "award_count"),
    sizeConfig: {
      large: 185,
      medium: 185,
      small: 120,
    },
  };

  const latestActivity: ColumnType<SupplierSummary> = {
    ...commonTableColumns.dateColumn,
    title: "Latest activity",
    render: (_: unknown, s: SupplierSummary) =>
      (awardStatsPerSupplier?.[s.id] && awardStatsPerSupplier[s.id].latest_activity) || EM_DASH,
    key: "latest_activity",
    sorter: true,
    sortOrder: getSortOrder(sort, "latest_activity"),
    sizeConfig: {
      // Has to be at least 120 otherwise you don't see the sorter
      xlarge: 140,
      large: 140,
      medium: 140,
      small: 120,
    },
  };

  const awardLink = {
    title: "View awards",
    key: "award_link",
    render: (_: unknown, s: SupplierSummary) => <ViewAwardsLink buyerGuid={buyerGuid} s={s} />,
  };

  return [signals, name, latestActivity, isSME, awardCount, awardLink];
}

const useDebounce200 = createUseDebounce(200);

const gqlQuery = graphql(`
  query suppliersForBuyerReport($request: SuppliersForBuyerReportRequest!) {
    suppliersForBuyerReport(request: $request) {
      suppliers {
        id
        guid
        name
        country
        isSme
      }
      additionalSupplierData {
        id
        data {
          awardCount
          latestActivity
          signals {
            id
            name
            category
            colour
          }
        }
      }
      pagingInfo {
        totalResults
        offset
        limit
      }
    }
  }
`);

export default function SuppliersTable({
  buyerId,
  buyerGuid,
  filters,
  initialSort,
  emptyStateMessage,
  handlePageChange,
  paginationState,
  companyGuid,
  teamId,
  rowSelectionConfig,
  excludeSignalCategories,
}: Props): JSX.Element {
  const [suppliers, setSuppliers] = React.useState<SupplierSummary[] | undefined>(undefined);
  const [awardsPerSupplier, setAwardsPerSupplier] = React.useState<AwardsPerSuppliers | undefined>(
    undefined,
  );
  const [sortState, setSortState] = React.useState(initialSort);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const { client } = useGraphQlClient();

  const handleOnRowSelect = React.useCallback(
    (ids: number[], items: SupplierSummary[]) => {
      if (!awardsPerSupplier) {
        return;
      }

      const selectedKeys = rowSelectionConfig.selectedRowKeys as number[];
      const newIds = ids.filter((id: number) => !selectedKeys.includes(id));
      const newItems: Record<string, SupplierSummary & AwardsPerSupplier> = {};
      for (const it of items) {
        // We're slightly misuing ant's table selection behaviour:
        // we pass in selectedKeys from multiple tables (because there could be multiple buyers selected)
        // but expect it to work correctly and pass back some sensible `items` for those keys.
        // Ant5 is stricter and passes `undefined` in `items` for the ids
        // that are not in the result set so we need to skip them.
        if (it === undefined) {
          continue;
        }

        if (newIds.includes(it.id)) {
          newItems[it.id] = { ...it, ...awardsPerSupplier[it.id] };
        }
      }

      rowSelectionConfig.onChange(ids, newItems);
    },
    [awardsPerSupplier, rowSelectionConfig],
  );

  const searchSuppliers = React.useCallback(async () => {
    if (!teamId) {
      throw new Error(`teamId is required to fetch suppliers for buyer report`);
    }

    try {
      setLoading(true);
      const offset = ((paginationState.current || 1) - 1) * (paginationState.pageSize || 0);

      const gqlResponse = await client.request<
        { suppliersForBuyerReport: SuppliersForBuyerReportResponse },
        { request: SuppliersForBuyerReportRequest }
      >(gqlQuery, {
        request: {
          buyerId: buyerGuid,
          text: filters.text,

          offset,
          limit: paginationState.pageSize,
          companyGuid: companyGuid,
          teamId,
          ...(excludeSignalCategories && { excludeSignalCategories: excludeSignalCategories }),
          ...(sortState.signal_sort && {
            signalSort:
              sortState.signal_sort === "ASC"
                ? SuppliersForBuyerReportRequest_SignalSortEnum_0.Asc
                : SuppliersForBuyerReportRequest_SignalSortEnum_0.Desc,
          }),
          sortOrder:
            sortState.order === "ASC"
              ? SuppliersForBuyerReportRequest_SortOrderEnum_0.Asc
              : SuppliersForBuyerReportRequest_SortOrderEnum_0.Desc,
          sort: sortState.field as SuppliersForBuyerReportRequest_SortEnum_0,
        },
      });

      if (!gqlResponse) {
        setLoading(false);
        return;
      }
      const { suppliers, additionalSupplierData, pagingInfo } = gqlResponse.suppliersForBuyerReport;
      setSuppliers(
        suppliers.map((s) => ({
          id: s.id,
          name: s.name,
          guid: s.guid,
          country: s.country ?? null,
          is_sme: s.isSme,
        })),
      );
      const supplierDataByNumericId: AwardsPerSuppliers = {};
      for (const data of additionalSupplierData) {
        const numericId = data.id;
        supplierDataByNumericId[numericId] = {
          award_count: data.data.awardCount,
          latest_activity: data.data.latestActivity,
          // the category type doesn't much 1005
          signals: data.data.signals as LeadSignal[],
        };
      }
      setAwardsPerSupplier(supplierDataByNumericId);
      setTotalCount(pagingInfo.totalResults);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setHasError(true);
      throw e;
    }
  }, [
    client,
    buyerGuid,
    filters,
    sortState,
    paginationState,
    companyGuid,
    teamId,
    excludeSignalCategories,
  ]);

  const debouncedSearchSuppliers = useDebounce200(searchSuppliers);

  React.useEffect(() => {
    debouncedSearchSuppliers();
  }, [buyerId, filters, sortState, paginationState, debouncedSearchSuppliers]);

  const handleChange = (
    newSorterState: SorterResult<SupplierSummary> | SorterResult<SupplierSummary>[],
  ) => {
    if (Array.isArray(newSorterState)) {
      newSorterState = newSorterState[0];
    }
    const newSortState: SortStateWithSignal = {
      order: newSorterState.order === "descend" ? "DESC" : "ASC",
      field: newSorterState.columnKey as string,
    };
    if (!(newSortState.order === sortState.order && newSortState.field === sortState.field)) {
      // If the selected sort column is lead signals we override the field to fall back to the original sort
      // and set the special signal_sort to its order
      if (newSortState.field === LEAD_SIGNAL_SORT_COLUMN) {
        newSortState.signal_sort = newSortState.order;
        newSortState.field = initialSort.field;
        newSortState.order = initialSort.order;
      }
      setSortState(newSortState);
    }
  };

  const footer = React.useCallback(() => {
    const searchFilters = {
      buyerIds: [buyerId],
    };
    return (
      <div className={css.tableFooter}>
        <Pagination {...paginationState} total={totalCount} onChange={handlePageChange} />
        <Button
          as="a"
          buttonType="tertiary"
          href={generateNoticeSearchUrl(convertRecordFiltersToNoticeFilters(searchFilters))}
          target="_blank"
        >
          See all
        </Button>
      </div>
    );
  }, [buyerId, totalCount, paginationState, handlePageChange]);

  const tableColumns = React.useMemo(
    () => getSupplierColumns(sortState, buyerGuid, awardsPerSupplier),
    [sortState, awardsPerSupplier, buyerGuid],
  );

  return (
    <Table<SupplierSummary>
      rowKey="id"
      pagination={false}
      loading={loading}
      dataSource={suppliers}
      columns={tableColumns}
      footer={footer}
      onChange={(_p, _f, sortState) => handleChange(sortState)}
      isError={hasError}
      locale={{
        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyStateMessage} />,
      }}
      rowSelection={
        {
          ...rowSelectionConfig,
          onChange: handleOnRowSelect,
          preserveSelectedRowKeys: true,
        } as AntTableRowSelection<SupplierSummary>
      }
      scroll={{ x: true }}
    />
  );
}
